import { z } from 'zod'

import ENDPOINTS from '@/config/http/endpoints'
import { parseResponse } from '@/config/http/http.helpers'
import {
  ApiBestSellingResponseSchema,
  ApiExportOrdersPerformanceResponseSchema,
  ApiOrdersPerformanceResponseSchema,
  ApiPreparationTimePerformanceSchema,
  ApiRevenuePerformanceData,
  ApiRevenueResponseSchema,
  ApiReviewsPerformanceResponseSchema
} from '@/ducks/performance/performance.schema'
import { rootApi } from '@/ducks/root.api'

export const performanceApi = rootApi.injectEndpoints({
  endpoints: build => {
    return {
      fetchOrdersPerformance: build.query<
        z.infer<typeof ApiOrdersPerformanceResponseSchema>,
        { from: number; to: number; store_id: string }
      >({
        providesTags: [
          { type: 'Performance', id: 'ORDERS' },
          { type: 'Orders' }
        ],
        query: ({ to, from, store_id }) => ({
          method: 'GET',
          url: ENDPOINTS.ORDERS_PERFORMANCE_LATEST(store_id),
          params: { to, from }
        }),
        transformResponse: (
          response: z.infer<typeof ApiOrdersPerformanceResponseSchema>
        ) => {
          return parseResponse(response, ApiOrdersPerformanceResponseSchema)
        }
      }),
      fetchPerformancePreparationTime: build.query<
        z.infer<typeof ApiPreparationTimePerformanceSchema>,
        { from: number; to: number; store_id: string }
      >({
        providesTags: [{ type: 'Performance', id: 'TIME' }, { type: 'Orders' }],
        query: ({ to, from, store_id }) => ({
          method: 'GET',
          url: ENDPOINTS.PREPARATION_TIME_LATEST(store_id),
          params: { to, from }
        }),
        transformResponse: (
          response: z.infer<typeof ApiPreparationTimePerformanceSchema>
        ) => {
          return parseResponse(response, ApiPreparationTimePerformanceSchema)
        }
      }),

      fetchReviewsPerformance: build.query<
        z.infer<typeof ApiReviewsPerformanceResponseSchema>,
        { from: number; to: number }
      >({
        providesTags: [
          { type: 'Performance', id: 'REVIEWS' },
          { type: 'Orders' }
        ],
        query: ({ to, from }) => ({
          method: 'GET',
          url: ENDPOINTS.REVIEWS_PERFORMANCE,
          params: { to, from }
        }),
        transformResponse: (
          response: z.infer<typeof ApiReviewsPerformanceResponseSchema>
        ) => {
          return parseResponse(response, ApiReviewsPerformanceResponseSchema)
        }
      }),

      fetchBestSellingPerformance: build.query<
        z.infer<typeof ApiBestSellingResponseSchema>,
        { from: number; to: number }
      >({
        providesTags: [
          { type: 'Performance', id: 'REVIEWS' },
          { type: 'Orders' }
        ],
        query: ({ to, from }) => ({
          method: 'GET',
          url: ENDPOINTS.BEST_SELLING_PERFORMANCE,
          params: { to, from }
        }),
        transformResponse: (
          response: z.infer<typeof ApiBestSellingResponseSchema>
        ) => {
          return parseResponse(response, ApiBestSellingResponseSchema)
        }
      }),

      fetchBranchBestSellingPerformance: build.query<
        z.infer<typeof ApiBestSellingResponseSchema>,
        { from: number; to: number; store_id: string }
      >({
        providesTags: [
          { type: 'Performance', id: 'REVIEWS' },
          { type: 'Orders' }
        ],
        query: ({ to, from, store_id }) => ({
          method: 'GET',
          url: ENDPOINTS.BRANCH_BEST_SELLING_PERFORMANCE(store_id),
          params: { to, from }
        }),
        transformResponse: (
          response: z.infer<typeof ApiBestSellingResponseSchema>
        ) => {
          return parseResponse(response, ApiBestSellingResponseSchema)
        }
      }),

      fetchRevenuePerformance: build.query<
        z.infer<typeof ApiRevenuePerformanceData>,
        { from: number; to: number }
      >({
        providesTags: [
          { type: 'Performance', id: 'REVENUE' },
          { type: 'Orders' }
        ],
        query: ({ to, from }) => ({
          method: 'GET',
          url: ENDPOINTS.REVENUE_PERFORMANCE,
          params: { to, from }
        }),
        transformResponse: (
          response: z.infer<typeof ApiRevenueResponseSchema>
        ) => {
          return parseResponse(response, ApiRevenueResponseSchema)?.data
        }
      }),

      fetchBranchRevenuePerformance: build.query<
        z.infer<typeof ApiRevenuePerformanceData>,
        {
          store_id: string
          from: number
          to: number
        }
      >({
        providesTags: [
          { type: 'Performance', id: 'REVENUE' },
          { type: 'Orders' }
        ],
        query: ({ store_id, to, from }) => ({
          method: 'GET',
          url: ENDPOINTS.BRANCH_REVENUE_PERFORMANCE({ store_id }),
          params: { to, from }
        }),
        transformResponse: (
          response: z.infer<typeof ApiRevenueResponseSchema>
        ) => {
          return parseResponse(response, ApiRevenueResponseSchema)?.data
        }
      }),

      fetchOrdersPerformanceOld: build.query<
        z.infer<typeof ApiOrdersPerformanceResponseSchema>,
        { from: number; to: number }
      >({
        providesTags: [
          { type: 'Performance', id: 'ORDERS_OLD' },
          { type: 'Orders' }
        ],
        query: ({ to, from }) => ({
          method: 'GET',
          url: ENDPOINTS.ORDERS_PERFORMANCE_OLD,
          params: { to, from }
        }),
        transformResponse: (
          response: z.infer<typeof ApiOrdersPerformanceResponseSchema>
        ) => {
          return parseResponse(response, ApiOrdersPerformanceResponseSchema)
        }
      }),
      fetchPreparationTimePerformanceOld: build.query<
        z.infer<typeof ApiPreparationTimePerformanceSchema>,
        { from: number; to: number }
      >({
        providesTags: [
          { type: 'Performance', id: 'TIME_OLD' },
          { type: 'Orders' }
        ],
        query: ({ to, from }) => ({
          method: 'GET',
          url: ENDPOINTS.PREPARATION_TIME_OLD,
          params: { to, from }
        }),
        transformResponse: (
          response: z.infer<typeof ApiPreparationTimePerformanceSchema>
        ) => {
          return parseResponse(response, ApiPreparationTimePerformanceSchema)
        }
      }),
      fetchBranchReviewsPerformance: build.query<
        {
          avg_client_rating: number
          avg_driver_rating: number
          client_rating_details: any[]
          driver_rating_details: any[]
        },
        { store_id: string; from: number; to: number }
      >({
        query: ({ store_id, from, to }) => ({
          method: 'GET',
          url: ENDPOINTS.BRANCH_REVIEWS_PERFORMANCE(store_id),
          params: { from, to }
        })
      }),
      fetchReviewsPerformanceOld: build.query<
        {
          avg_client_rating: number
          avg_driver_rating: number
          client_rating_details: any[]
          driver_rating_details: any[]
        },
        { from: number; to: number }
      >({
        query: ({ from, to }) => ({
          method: 'GET',
          url: ENDPOINTS.REVIEWS_PERFORMANCE,
          params: { from, to }
        })
      }),
      exportOrdersPerformance: build.query<
        z.infer<typeof ApiExportOrdersPerformanceResponseSchema>,
        { from: number; to: number; }
      >({
        query: ({ from, to }) => ({
          method: 'GET',
          url: ENDPOINTS.EXPORT_ORDERS_PERFORMANCE,
          params: { from, to }
        })
      })
    }
  }
})

const {
  useLazyFetchOrdersPerformanceQuery,
  useLazyFetchPerformancePreparationTimeQuery,
  useLazyFetchReviewsPerformanceQuery,
  useLazyFetchBestSellingPerformanceQuery,
  useLazyFetchBranchBestSellingPerformanceQuery,
  useLazyFetchRevenuePerformanceQuery,
  useLazyFetchBranchRevenuePerformanceQuery,
  useLazyFetchOrdersPerformanceOldQuery,
  useLazyFetchPreparationTimePerformanceOldQuery,
  useLazyFetchBranchReviewsPerformanceQuery,
  useLazyFetchReviewsPerformanceOldQuery,
  useLazyExportOrdersPerformanceQuery
} = performanceApi

export {
  useLazyFetchBestSellingPerformanceQuery,
  useLazyFetchBranchBestSellingPerformanceQuery,
  useLazyFetchBranchRevenuePerformanceQuery,
  useLazyFetchOrdersPerformanceQuery,
  useLazyFetchPerformancePreparationTimeQuery,
  useLazyFetchRevenuePerformanceQuery,
  useLazyFetchReviewsPerformanceQuery,
  useLazyFetchOrdersPerformanceOldQuery,
  useLazyFetchPreparationTimePerformanceOldQuery,
  useLazyFetchBranchReviewsPerformanceQuery,
  useLazyFetchReviewsPerformanceOldQuery,
  useLazyExportOrdersPerformanceQuery
}
